import React, { useEffect, useState } from 'react';
import { Bitcoin, Search, TrendingUp } from 'lucide-react';
// ✅ Import ฟังก์ชันใหม่ที่สร้างใน fetchAPI.tsx (แก้ path ให้ตรงกับโครงสร้างไฟล์ของคุณด้วยนะครับ)
import { searchMasterAssets } from '../composables/fetchAPI';
import {
  searchCryptoInstruments,
  fetchCryptoInstruments,
  getCryptoCatalogStatus,
  CryptoInstrument,
  CryptoCatalogStatus, cryptoPairLabel } from '../composables/fetchCrypto';

type SearchMode = 'stock' | 'crypto';

interface SearchBarProps {
  onSelect: (symbol: string) => void;
  selectedSymbols?: string[]; // แสดง ✔ หลังหุ้นที่ถูกเลือกแล้ว (ใช้กับโหมดเลือกหลายตัว)
  persistSelection?: boolean; // false = ไม่บันทึก selectedSymbol ลง localStorage
  includeCrypto?: boolean; // true = มีปุ่มสลับ หุ้น / Crypto (ปิดไว้เป็นค่าเริ่มต้น หน้าพอร์ต/บทความยังไม่รองรับ crypto)
  compactToggle?: boolean; // true = ปุ่มสลับแบบไอคอนวางข้าง input (ใช้กับ header desktop ที่พื้นที่แคบ)
}

interface AssetResult {
  match_score: number | null;
  assetType: string;
  displaySymbol: string;
  displayName: string;
  providerSymbol: string;
  subtitle: string;
}

interface SearchItem {
  symbol: string;        // ค่าที่ส่งออกไปตอนเลือก (crypto = X:BTCUSD)
  displaySymbol: string; // ค่าที่โชว์ (crypto = BTC)
  name: string;
  subtitle: string;
}

// เหรียญยอดนิยมที่โชว์ตอนยังไม่พิมพ์ (โชว์เฉพาะตัวที่มีใน catalog จริง)
const SUGGESTED_CRYPTO = ['BTC', 'ETH', 'SOL', 'XRP', 'BNB', 'DOGE', 'ADA'];

const MODE_STYLES: Record<SearchMode, {
  activePill: string;
  inputBorder: string;
  dropdownBorder: string;
  hover: string;
  suggestHeader: string;
  badge: string;
  badgeLabel: string;
  placeholder: string;
  suggestTitle: string;
  emptyLabel: string;
}> = {
  stock: {
    activePill: 'bg-green-500 text-white shadow',
    inputBorder: 'border-green-500 focus:ring-green-400',
    dropdownBorder: 'border-green-500',
    hover: 'hover:bg-green-50',
    suggestHeader: 'bg-green-50 border-green-100',
    badge: 'bg-green-50 text-green-700 border-green-200',
    badgeLabel: 'หุ้น',
    placeholder: 'ค้นหาหุ้นเช่น NVDA, MSFT, AAPL',
    suggestTitle: '🔥 หุ้นตัวโปรด',
    emptyLabel: 'ไม่พบหุ้น',
  },
  crypto: {
    activePill: 'bg-orange-500 text-white shadow',
    inputBorder: 'border-orange-500 focus:ring-orange-400',
    dropdownBorder: 'border-orange-500',
    hover: 'hover:bg-orange-50',
    suggestHeader: 'bg-orange-50 border-orange-100',
    badge: 'bg-orange-50 text-orange-700 border-orange-200',
    badgeLabel: 'Crypto',
    placeholder: 'ค้นหาเหรียญเช่น BTC, ETH, SOL',
    suggestTitle: '🔥 เหรียญยอดนิยม',
    emptyLabel: 'ไม่พบเหรียญ',
  },
};

function getStoredMode(): SearchMode {
  try {
    return localStorage.getItem('searchMode') === 'crypto' ? 'crypto' : 'stock';
  } catch {
    return 'stock';
  }
}

const toStockItems = (items: AssetResult[]): SearchItem[] =>
  items.map(item => ({
    symbol: item.displaySymbol,
    displaySymbol: item.displaySymbol,
    name: item.displayName,
    subtitle: item.subtitle,
  }));

const toCryptoItems = (items: CryptoInstrument[]): SearchItem[] =>
  items.map(item => ({
    symbol: item.symbol,
    displaySymbol: cryptoPairLabel(item.symbol), // BTC/USD ให้เหมือนกันทั้งเว็บ
    name: item.name,
    subtitle: item.pair,
  }));

const SearchBar = ({ onSelect, selectedSymbols = [], persistSelection = true, includeCrypto = false, compactToggle = false }: SearchBarProps) => {
  const [mode, setMode] = useState<SearchMode>(() => (includeCrypto ? getStoredMode() : 'stock'));
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchItem[]>([]);
  const [suggested, setSuggested] = useState<Record<SearchMode, SearchItem[]>>({ stock: [], crypto: [] });
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [typingTimeout, setTypingTimeout] = useState<NodeJS.Timeout | null>(null);
  const [catalogStatus, setCatalogStatus] = useState<CryptoCatalogStatus>(getCryptoCatalogStatus);

  // โหลด catalog ไว้ก่อน จะได้รู้ว่า server เปิด crypto อยู่ไหม (ถ้าปิด ซ่อนปุ่ม Crypto ไปเลย)
  useEffect(() => {
    if (!includeCrypto) return;
    let cancelled = false;
    fetchCryptoInstruments().then(() => {
      if (cancelled) return;
      const status = getCryptoCatalogStatus();
      setCatalogStatus(status);
      if (status === 'disabled') setMode('stock');
    });
    return () => { cancelled = true; };
  }, [includeCrypto]);

  const showCryptoToggle = includeCrypto && catalogStatus !== 'disabled';
  const cryptoUnavailable = mode === 'crypto' && catalogStatus === 'unavailable';

  const styles = MODE_STYLES[mode];

  const search = async (value: string, searchMode: SearchMode): Promise<SearchItem[]> => {
    if (searchMode === 'crypto') {
      const items = toCryptoItems(await searchCryptoInstruments(value));
      setCatalogStatus(getCryptoCatalogStatus());
      return items;
    }
    // ✅ เรียกใช้ API ผ่าน fetchAPI พร้อมส่งคำค้นหา
    const response = await searchMasterAssets(value);
    return toStockItems(response.success && response.items ? response.items : []);
  };

  // ดึง Suggestion เมื่อไม่มีการพิมพ์
  const loadSuggestions = async (searchMode: SearchMode) => {
    if (suggested[searchMode].length > 0) return;

    setSuggestLoading(true);
    try {
      let items: SearchItem[];
      if (searchMode === 'crypto') {
        const all = await searchCryptoInstruments('', 1000);
        items = toCryptoItems(
          SUGGESTED_CRYPTO
            .map(base => all.find(i => i.baseAsset.toUpperCase() === base))
            .filter((i): i is CryptoInstrument => !!i)
        );
        setCatalogStatus(getCryptoCatalogStatus());
      } else {
        const response = await searchMasterAssets();
        items = toStockItems(response.success && response.items ? response.items : []);
      }
      setSuggested(prev => ({ ...prev, [searchMode]: items }));
    } catch (error) {
      console.error('Error loading suggestions:', error);
    } finally {
      setSuggestLoading(false);
    }
  };

  const runSearch = (value: string, searchMode: SearchMode) => {
    if (typingTimeout) {
      clearTimeout(typingTimeout);
    }

    if (value.length < 1) {
      setResults([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    const timeout = setTimeout(async () => {
      try {
        setResults(await search(value, searchMode));
        setShowDropdown(true);
      } catch (error) {
        console.error('Error searching assets:', error);
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);

    setTypingTimeout(timeout);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.trim().toUpperCase();
    setQuery(value);
    runSearch(value, mode);
  };

  const handleModeChange = (next: SearchMode) => {
    if (next === mode) return;
    setMode(next);
    try {
      localStorage.setItem('searchMode', next);
    } catch { }
    setResults([]);
    // ค้นคำเดิมซ้ำในโหมดใหม่ ผู้ใช้ไม่ต้องพิมพ์ใหม่
    if (query.length > 0) runSearch(query, next);
    else loadSuggestions(next);
  };

  const handleFocus = async () => {
    if (query.length === 0) {
      await loadSuggestions(mode);
    }
    setShowDropdown(true);
  };

  const handleSelect = (symbol: string) => {
    setQuery('');
    setResults([]);
    setShowDropdown(false);
    if (persistSelection) localStorage.setItem('selectedSymbol', symbol);
    onSelect(symbol);
  };

  const isSuggestMode = query.length === 0;
  const displayList = isSuggestMode ? suggested[mode] : results;
  const isLoadingActive = isSuggestMode ? suggestLoading : loading;

  // onMouseDown + preventDefault กันไม่ให้ input เสีย focus (ไม่งั้น dropdown ปิดก่อนสลับโหมด)
  const renderToggle = () => (
    <div className={`flex items-center rounded-full bg-gray-800/80 p-1 gap-1 ${compactToggle ? '' : 'self-start'}`}>
      {(['stock', 'crypto'] as SearchMode[]).map(m => {
        const active = mode === m;
        const Icon = m === 'stock' ? TrendingUp : Bitcoin;
        return (
          <button
            key={m}
            type='button'
            title={m === 'stock' ? 'หุ้น' : 'Crypto'}
            onMouseDown={e => { e.preventDefault(); handleModeChange(m); }}
            className={`flex items-center gap-1 rounded-full text-xs font-semibold transition-colors ${compactToggle ? 'p-1.5' : 'px-3 py-1'} ${active ? MODE_STYLES[m].activePill : 'text-gray-400 hover:text-gray-200'}`}
          >
            <Icon className='w-3.5 h-3.5' />
            {!compactToggle && <span>{m === 'stock' ? 'หุ้น' : 'Crypto'}</span>}
          </button>
        );
      })}
    </div>
  );

  const input = (
    <div className='relative w-full'>
      {includeCrypto && (
        <Search className='absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none' />
      )}
      <input
        className={`w-full pr-4 py-2 text-gray-900 rounded-lg focus:outline-none focus:ring-2 ${includeCrypto ? `pl-9 border-2 bg-white ${styles.inputBorder}` : 'px-4 border border-gray-400 focus:ring-yellow-400'}`}
        type='text'
        placeholder={styles.placeholder}
        value={query}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
      />
    </div>
  );

  return (
    <div className={`relative w-full ${compactToggle ? '' : 'max-w-md'}`}>
      {showCryptoToggle ? (
        <div className={compactToggle ? 'flex items-center gap-2' : 'flex flex-col gap-2'}>
          {renderToggle()}
          {input}
        </div>
      ) : input}

      {showDropdown && (
        <ul className={`absolute left-0 right-0 bg-white border rounded-lg shadow-lg mt-1 z-10 max-h-60 overflow-y-auto ${includeCrypto ? `border-2 ${styles.dropdownBorder}` : 'border-gray-300'} ${compactToggle ? 'min-w-[260px]' : ''}`}>

          {isSuggestMode && !suggestLoading && displayList.length > 0 && (
            <li className={`px-4 py-2 text-xs text-gray-500 font-bold sticky top-0 border-b z-20 ${includeCrypto ? styles.suggestHeader : 'bg-yellow-50 border-yellow-100'}`}>
              {styles.suggestTitle}
            </li>
          )}

          {isLoadingActive && (
            <li className='px-4 py-3 text-sm text-gray-400 text-center'>กำลังโหลดข้อมูล...</li>
          )}

          {!isLoadingActive && displayList.map((item) => (
            <li
              key={item.symbol}
              className={`px-4 py-3 cursor-pointer text-gray-900 flex items-center justify-between border-b border-gray-50 last:border-0 ${includeCrypto ? styles.hover : 'hover:bg-yellow-50'}`}
              onMouseDown={() => handleSelect(item.symbol)}
            >
              <div className='flex items-center gap-3 overflow-hidden'>
                {isSuggestMode && !includeCrypto && <span title="Suggested" className="text-yellow-400 text-lg">★</span>}
                <div className='flex flex-col overflow-hidden'>
                  <span className='font-bold text-sm'>{item.displaySymbol}</span>
                  <span className='text-gray-500 text-xs truncate w-48'>{item.name}</span>
                </div>
              </div>

              <div className='flex items-center gap-2 ml-2'>
                {selectedSymbols.includes(item.symbol) && (
                  <span className='text-green-600 font-bold'>✔</span>
                )}
                {includeCrypto && (
                  <span className={`text-[10px] font-semibold border rounded-full px-2 py-0.5 whitespace-nowrap ${styles.badge}`}>
                    {styles.badgeLabel}
                  </span>
                )}
              </div>
            </li>
          ))}

          {!isLoadingActive && cryptoUnavailable && displayList.length === 0 && (
            <li className='px-4 py-3 text-sm text-orange-600 text-center'>
              ระบบ Crypto ยังไม่พร้อมใช้งาน ลองใหม่อีกครั้งในอีกสักครู่
            </li>
          )}

          {!isLoadingActive && !cryptoUnavailable && !isSuggestMode && displayList.length === 0 && (
            <li className='px-4 py-3 text-sm text-gray-400 text-center'>
              {styles.emptyLabel} "{query}"
            </li>
          )}
        </ul>
      )}
    </div>
  );
};

export default SearchBar;
